/**
 * Stable `data-testid` values for automation testing.
 *
 * Ids follow `<module>-<context>-<element>-<action|field>` in lowercase
 * kebab-case English, regardless of the Indonesian UI copy. They are additive:
 * nothing here replaces an `aria-label`, a `<label>`, or an existing `id` used
 * for `htmlFor` or anchor navigation.
 *
 * The attribute is kept in production builds so the same suite can run against
 * a preview or staging deployment.
 *
 * See `openspec/docs/auto-id-test.md` for the naming rules and the id catalog.
 */

type IdPart = string | number | false | null | undefined

/** Joins the non-empty parts into a kebab-case test id. */
export function testId(...parts: IdPart[]): string {
  return parts.filter((part) => part !== false && part != null && part !== '').join('-')
}

/**
 * Normalises a backend identifier into an id segment.
 *
 * Menu codes arrive dotted (`spmb.dashboard`) and emails contain dots and an
 * `@`; both become kebab-case so they can sit inside a test id unchanged.
 */
export function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/**
 * Builds the `data-testid` prop, or nothing when no prefix was supplied.
 *
 * Returning an empty object rather than `data-testid: undefined` keeps the
 * attribute off the DOM entirely for callers that opted out.
 */
export function testIdProps(...parts: IdPart[]): { 'data-testid'?: string } {
  const id = testId(...parts)
  return id ? { 'data-testid': id } : {}
}

/**
 * Ids for a hand-rolled `<table>`, matching what `DataTable` emits.
 *
 * 39 files render their own `<table>` instead of going through `DataTable`.
 * They spread these props so both kinds of table expose the same contract to
 * the test suite without needing a migration first.
 */
export function tableTestIds(prefix: string | undefined) {
  return {
    table: testIdProps(prefix, 'table'),
    header: (index: number) => testIdProps(prefix, 'th', index),
    row: (index: number) => testIdProps(prefix, 'table-row', index),
    cell: (index: number, name: string) => testIdProps(prefix, 'table-row', index, `${name}-cell`),
    loading: testIdProps(prefix, 'table-loading'),
    empty: testIdProps(prefix, 'table-empty'),
    error: testIdProps(prefix, 'table-error'),
  }
}
